import React, { useState, useMemo } from 'react';
import {
  Typography, Card, Table, Tag, Button, Space, Select, Statistic, Row, Col, Tooltip, Switch,
} from 'antd';
import {
  ReloadOutlined, ShoppingOutlined, DollarOutlined, CreditCardOutlined, WarningOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { analyticsApi } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

interface DailyCell {
  date: string;
  completed_amount: number;
  completed_count: number;
  unpaid_amount: number;
  unpaid_count: number;
}

interface ChannelRow {
  payment_method: string;
  name: string;
  daily: DailyCell[];
  completed_amount: number;
  completed_count: number;
  unpaid_amount: number;
  unpaid_count: number;
  refunded_amount: number;
  refunded_count: number;
  processing_amount: number;
  processing_count: number;
  success_rate: number | null;
  unique_completed_count: number;
  unique_unpaid_count: number;
  unique_success_rate: number | null;
  avg_order_amount: number | null;
}

interface Totals {
  completed_amount: number;
  completed_count: number;
  unpaid_amount: number;
  unpaid_count: number;
  refunded_amount: number;
  refunded_count: number;
  success_rate: number | null;
  unique_completed_count: number;
  unique_unpaid_count: number;
  unique_success_rate: number | null;
}

interface ChannelPayload {
  days: string[];
  channels: ChannelRow[];
  totals: Totals;
}

/** 成功率色阶：>=70 绿、>=40 黄、其余红；无数据灰 */
const rateTag = (r?: number | null) => {
  if (r === null || r === undefined) return <Text type="secondary">—</Text>;
  const color = r >= 70 ? 'green' : r >= 40 ? 'gold' : 'red';
  return <Tag color={color}>{r.toFixed(1)}%</Tag>;
};

const money = (v: number) =>
  `¥${(v || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ChannelAnalysis: React.FC = () => {
  const [days, setDays] = useState<number>(7);
  const [metric, setMetric] = useState<'amount' | 'count'>('amount');
  const [onlyActive, setOnlyActive] = useState<boolean>(true);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['channel-analysis', days],
    queryFn: async () => {
      const res = await analyticsApi.getChannels({ days });
      return (res as unknown as ChannelPayload) || null;
    },
  });

  const allChannels: ChannelRow[] = data?.channels || [];
  const totals: Totals | undefined = data?.totals;

  const rows = useMemo(
    () => (onlyActive
      ? allChannels.filter((c) => c.completed_count > 0 || c.unpaid_count > 0)
      : allChannels),
    [allChannels, onlyActive],
  );

  const activeChannelCount = allChannels.filter((c) => c.completed_count > 0).length;

  // 每日各通道成交额堆叠柱状图（只画有成交的通道）
  const chartOption = useMemo(() => {
    const daysArr = data?.days || [];
    const active = allChannels.filter((c) => c.completed_amount > 0);
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (v: number) => `¥${Number(v || 0).toLocaleString('zh-CN')}`,
      },
      legend: { type: 'scroll', bottom: 0 },
      grid: { left: 70, right: 24, top: 24, bottom: 52 },
      xAxis: { type: 'category', data: daysArr.map((d) => d.slice(5)) },
      yAxis: {
        type: 'value',
        name: '成交额(¥)',
        axisLabel: { formatter: (v: number) => (v >= 10000 ? `${v / 10000}万` : `${v}`) },
      },
      series: active.map((c) => ({
        name: c.name,
        type: 'bar',
        stack: 'total',
        emphasis: { focus: 'series' },
        barMaxWidth: 42,
        data: c.daily.map((d) => d.completed_amount),
      })),
    };
  }, [data, allChannels]);

  const columns: any[] = useMemo(() => {
    const dayCols = (data?.days || []).map((d, idx) => ({
      title: d.slice(5),
      key: `d-${d}`,
      width: 104,
      align: 'right' as const,
      render: (_: any, r: ChannelRow) => {
        const cell = r.daily[idx];
        if (!cell) return <Text type="secondary">—</Text>;
        if (metric === 'count') {
          return cell.completed_count > 0
            ? <Text>{cell.completed_count}</Text>
            : <Text type="secondary">—</Text>;
        }
        return cell.completed_amount > 0 ? (
          <Tooltip title={`${cell.completed_count} 笔 · 未付 ${cell.unpaid_count} 笔`}>
            <Text>{(cell.completed_amount / 1000).toFixed(1)}k</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    }));

    return [
      {
        title: '支付通道',
        key: 'channel',
        width: 180,
        fixed: 'left' as const,
        render: (_: any, r: ChannelRow) => (
          <div>
            <Text strong>{r.name}</Text>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{r.payment_method}</Text></div>
          </div>
        ),
      },
      ...dayCols,
      {
        title: metric === 'count' ? '成交笔数' : '成交额',
        key: 'completed',
        width: 130,
        align: 'right' as const,
        fixed: 'right' as const,
        render: (_: any, r: ChannelRow) => (
          <Text strong style={{ color: '#722ed1' }}>
            {metric === 'count' ? `${r.completed_count} 笔` : money(r.completed_amount)}
          </Text>
        ),
      },
      {
        title: '未付(流失)',
        key: 'unpaid',
        width: 140,
        align: 'right' as const,
        render: (_: any, r: ChannelRow) => (
          <Tooltip title={`${r.unpaid_count} 笔未付款订单`}>
            <Text type="secondary">{money(r.unpaid_amount)}</Text>
          </Tooltip>
        ),
      },
      {
        title: '成功率（有效）',
        key: 'rate',
        width: 112,
        align: 'center' as const,
        sorter: (a: ChannelRow, b: ChannelRow) =>
          (a.unique_success_rate || 0) - (b.unique_success_rate || 0),
        render: (_: any, r: ChannelRow) => (
          <Tooltip
            title={`有效口径：同一客户对同一商品多次重试只算一次｜按订单笔数：${r.success_rate ?? '—'}%`}
          >
            {rateTag(r.unique_success_rate)}
          </Tooltip>
        ),
      },
      {
        title: '客单价',
        key: 'avg',
        width: 104,
        align: 'right' as const,
        render: (_: any, r: ChannelRow) =>
          r.avg_order_amount ? <Text>{money(r.avg_order_amount)}</Text> : <Text type="secondary">—</Text>,
      },
    ];
  }, [data, metric]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title level={2} className="mb-2">🔀 通道分析</Title>
          <Text type="secondary">
            近 {days} 天各支付通道的成交额与流失率 · 成交=COMPLETED，未付=已取消/已过期，成功率=成交笔数/(成交+未付)
          </Text>
        </div>
        <Space>
          <Select value={days} onChange={setDays} style={{ width: 120 }}>
            <Option value={7}>近 7 天</Option>
            <Option value={14}>近 14 天</Option>
            <Option value={30}>近 30 天</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>刷新</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="总成交额"
              value={totals?.completed_amount || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="总成交笔数"
              value={totals?.completed_count || 0}
              prefix={<ShoppingOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="整体成功率（有效）"
              value={totals?.unique_success_rate ?? 0}
              precision={1}
              suffix="%"
              prefix={<CreditCardOutlined style={{ color: totals && totals.unique_success_rate !== null && totals.unique_success_rate < 40 ? '#ff4d4f' : '#52c41a' }} />}
              valueStyle={{
                color: totals && totals.unique_success_rate !== null && totals.unique_success_rate < 40 ? '#ff4d4f' : '#52c41a',
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="有成交的通道"
              value={activeChannelCount}
              suffix={`/ ${allChannels.length}`}
              prefix={<WarningOutlined style={{ color: '#faad14' }} />}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" title={`近 ${days} 天各通道成交额（堆叠）`}>
        <ReactECharts option={chartOption} style={{ height: 340 }} notMerge />
      </Card>

      <Card
        size="small"
        title="通道 × 日期 明细"
        extra={
          <Space>
            <span>日粒度：</span>
            <Select
              value={metric}
              onChange={(v) => setMetric(v)}
              size="small"
              style={{ width: 100 }}
            >
              <Option value="amount">成交额</Option>
              <Option value="count">成交笔数</Option>
            </Select>
            <span style={{ marginLeft: 8 }}>仅显示有数据：</span>
            <Switch size="small" checked={onlyActive} onChange={setOnlyActive} />
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="payment_method"
          loading={isLoading}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
        {metric === 'amount' && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              表格内日数据单位为千元(k)，鼠标悬停可看笔数明细；未付 = 用户选了该通道但最终未付款的订单金额
            </Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ChannelAnalysis;
